import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AvailableVariableNamespace } from '../../../pages/templates/models/template.model';

interface AttachmentItem {
  url: string;
  fileName: string;
  uploading?: boolean;
}

/** INC 82 — vistas del editor: diseño (WYSIWYG), código HTML y previa renderizada. */
export type EditorMode = 'design' | 'html' | 'preview';

/**
 * Editor de texto enriquecido simple (negrita, listas, links),
 * tipo compositor de Gmail: se edita directamente sobre el contenido, sin
 * paneles de bloques ni navegación a otra pantalla.
 *
 * Para layouts visuales con columnas/CTA/hero se usa el editor avanzado
 * (GrapesJS, `app-grapes-email-editor`) — este componente es la vía rápida
 * para campañas de texto simple.
 *
 * Los archivos adjuntos (PDF, Word, Excel, ...) se manejan FUERA del
 * contenteditable, en una franja propia arriba del cuerpo (estilo
 * Hotmail/Outlook): insertarlos dentro del contenido rico — sobre todo
 * dentro de listas — corrompía el DOM y bloqueaba la escritura. Las
 * imágenes sí van inline en el cuerpo, igual que en cualquier compositor.
 */
@Component({
  selector: 'app-rich-text-editor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './rich-text-editor.component.html',
  styles: [`
    /* Tailwind preflight resetea list-style en ul/ol; el contenido se
       inserta vía innerHTML (no pasa por el template), así que necesita
       ::ng-deep para que el override aplique. */
    :host ::ng-deep .rte-editable ul {
      list-style: disc;
      padding-left: 1.5rem;
      margin: 0.5rem 0;
    }
    :host ::ng-deep .rte-editable ol {
      list-style: decimal;
      padding-left: 1.5rem;
      margin: 0.5rem 0;
    }
    :host ::ng-deep .rte-editable li {
      display: list-item;
    }
    :host ::ng-deep .rte-editable a {
      color: #465fff;
      text-decoration: underline;
    }
    /* INC 65: preflight también aplana h1/h2/h3 y blockquote. Sin esto el
       botón de estilo de bloque "no hace nada" a la vista. */
    :host ::ng-deep .rte-editable h1 {
      font-size: 1.6em;
      font-weight: 700;
      line-height: 1.25;
      margin: 0.6em 0 0.3em;
    }
    :host ::ng-deep .rte-editable h2 {
      font-size: 1.3em;
      font-weight: 700;
      line-height: 1.3;
      margin: 0.6em 0 0.3em;
    }
    :host ::ng-deep .rte-editable h3 {
      font-size: 1.1em;
      font-weight: 600;
      line-height: 1.35;
      margin: 0.6em 0 0.3em;
    }
    :host ::ng-deep .rte-editable blockquote {
      border-left: 3px solid #d1d5db;
      padding-left: 0.85rem;
      margin: 0.5rem 0;
      color: #6b7280;
    }
    :host ::ng-deep .rte-editable .merge-tag-chip {
      display: inline-block;
      padding: 1px 8px;
      background: #5b21b6;
      color: #ffffff;
      border-radius: 9999px;
      font-family: ui-monospace, monospace;
      font-size: 0.85em;
      font-weight: 600;
      line-height: 1.6;
      user-select: all;
    }
    /* Controles de imagen (cambiar/quitar): ocultos hasta hover o selección,
       para no estorbar la lectura del contenido mientras se escribe. */
    :host ::ng-deep .rte-editable [data-rte-image] [data-rte-editor-only] {
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.15s ease;
    }
    :host ::ng-deep .rte-editable [data-rte-image]:hover [data-rte-editor-only],
    :host ::ng-deep .rte-editable [data-rte-image].rte-image-selected [data-rte-editor-only] {
      opacity: 1;
      pointer-events: auto;
    }
    /* Outline visible mientras la imagen está seleccionada — confirma qué
       imagen se va a quitar/cambiar antes de hacer clic en los controles. */
    :host ::ng-deep .rte-editable [data-rte-image].rte-image-selected > img {
      outline: 2px solid #465fff;
      outline-offset: 2px;
    }
  `],
})
export class RichTextEditorComponent implements AfterViewInit, OnChanges {
  @ViewChild('editable', { static: true }) editableRef!: ElementRef<HTMLDivElement>;
  @ViewChild('linkInput') linkInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('variablesBtn') variablesBtnRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('variablesSearch') variablesSearchRef?: ElementRef<HTMLInputElement>;
  @ViewChild('imageInput') imageInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('attachmentInput') attachmentInputRef?: ElementRef<HTMLInputElement>;
  /** INC 82 — iframe aislado de la vista previa. */
  @ViewChild('previewFrame') previewFrameRef?: ElementRef<HTMLIFrameElement>;

  @Input() value = '';
  @Input() placeholder = 'Escribe el contenido de tu correo...';
  @Input() disabled = false;
  @Input() minHeight = '320px';
  @Input() variableNamespaces: AvailableVariableNamespace[] = [];
  /** Si no se provee, el botón de insertar imagen no se muestra. */
  @Input() uploadHandler?: (file: File) => Promise<string>;
  /** Si no se provee, el botón de adjuntar archivo no se muestra. */
  @Input() attachmentUploadHandler?: (file: File) => Promise<{ url: string; fileName: string }>;

  @Output() valueChange = new EventEmitter<string>();

  /**
   * INC 82 — modo del editor.
   *
   * El componente era solo WYSIWYG: quien pegaba una estructura HTML la veía convertirse en
   * texto (el navegador inserta el portapapeles de texto plano como texto, no como marcado),
   * se guardaba escapada y el destinatario recibía las etiquetas a la vista. Ahora hay tres
   * vistas, como en el resto de módulos: diseño, código y previa renderizada.
   */
  mode: EditorMode = 'design';
  /** Fuente HTML editable mientras `mode === 'html'`. */
  htmlSource = '';
  /**
   * INC 82 — el cuerpo guardado parece HTML escapado (etiquetas visibles como texto).
   * Se ofrece convertirlo, porque las plantillas creadas antes del arreglo quedaron así.
   */
  escapedHtmlDetected = false;

  variablesMenuOpen = false;
  /** Abre el menú hacia arriba cuando no hay sitio debajo (formularios largos / drawer). */
  variablesMenuDropUp = false;
  /** Alto máximo de la lista de variables; se recalcula cada vez que se abre el menú. */
  variablesMenuListMaxHeight = '20rem';
  /** Filtro del menú de variables — los catálogos pasan de 20 tags. */
  variableSearch = '';
  linkPopoverOpen = false;
  linkUrl = '';
  /** INC 65 — paleta de color de texto abierta. */
  colorMenuOpen = false;

  /** INC 65 — estilos de bloque del selector de formato. */
  readonly blockFormats: { value: string; label: string }[] = [
    { value: 'p', label: 'Párrafo' },
    { value: 'h1', label: 'Título 1' },
    { value: 'h2', label: 'Título 2' },
    { value: 'h3', label: 'Título 3' },
    { value: 'blockquote', label: 'Cita' },
  ];

  /** INC 65 — colores de texto (suficientes para un correo, sin selector nativo). */
  readonly textColors: { value: string; label: string }[] = [
    { value: '#111827', label: 'Negro' },
    { value: '#6b7280', label: 'Gris' },
    { value: '#465fff', label: 'Azul' },
    { value: '#16a34a', label: 'Verde' },
    { value: '#dc2626', label: 'Rojo' },
    { value: '#ea580c', label: 'Naranja' },
    { value: '#7c3aed', label: 'Morado' },
  ];
  imageUploading = false;
  imageUploadError = '';
  attachmentUploadError = '';
  /** Resalta el área editable mientras se arrastra un archivo por encima (estilo Gmail). */
  dragOver = false;
  /** Franja de adjuntos arriba del cuerpo (estilo Hotmail) — vive fuera del contenteditable. */
  attachments: AttachmentItem[] = [];

  private emitTimer: ReturnType<typeof setTimeout> | null = null;
  private lastEmitted = '';
  private viewReady = false;
  private savedRange: Range | null = null;
  /** Imagen actualmente seleccionada en el editor (outline + controles visibles). */
  private selectedImageWrapper: HTMLElement | null = null;
  /** Si está seteado, el próximo archivo elegido en imageInput reemplaza esta imagen en vez de insertar una nueva. */
  private replaceTargetWrapper: HTMLElement | null = null;

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.setContent(this.value);
    this.applyDisabled();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady) return;
    if (changes['disabled']) this.applyDisabled();
    if (changes['value'] && !changes['value'].firstChange) {
      const incoming = (this.value ?? '').trim();
      // Si el cambio viene de nuestro propio emit (round-trip del padre),
      // no reescribimos el DOM — perdería la posición del caret.
      if (incoming === this.lastEmitted.trim()) return;
      const current = this.buildFullHtml().trim();
      if (incoming !== current) {
        this.setContent(this.value);
        // INC 82: si el padre carga otra plantilla con la vista de código abierta, el
        // textarea tiene que reflejar el contenido nuevo, no el anterior.
        if (this.mode === 'html') this.htmlSource = this.currentBodyHtml();
        if (this.mode === 'preview') this.renderPreview();
      }
    }
  }

  /**
   * Booleano explícito (no un getter leyendo el DOM en cada change detection):
   * un getter acá dispara NG0100 porque el contenido editable puede mutar
   * fuera del ciclo de Angular (escritura nativa del navegador) entre
   * el primer y segundo check de la misma pasada.
   */
  empty = true;

  /** Separa la franja de adjuntos (si la trae el HTML guardado) del cuerpo, y carga cada uno por su lado. */
  private setContent(html: string): void {
    const { attachments, bodyHtml } = this.parseIncomingHtml(html || '');
    this.attachments = attachments;
    this.editableRef.nativeElement.innerHTML = bodyHtml;
    this.decorateImageControls();
    this.updateEmpty();
    this.detectEscapedHtml();
  }

  // ── INC 82: diseño / HTML / vista previa ──────────────────────────

  /**
   * Cambia de vista sincronizando el contenido en la dirección correcta.
   *
   * Al ENTRAR en "HTML" se vuelca el cuerpo real del editor al textarea; al SALIR se
   * reinyecta el texto del textarea como marcado (`innerHTML`, no `textContent`: ese era
   * justamente el bug — el contenido acababa escapado y el correo mostraba las etiquetas).
   */
  setMode(next: EditorMode): void {
    if (this.mode === next) return;
    if (this.mode === 'html') {
      this.applyHtmlSource();
    }
    if (next === 'html') {
      this.htmlSource = this.currentBodyHtml();
    }
    this.mode = next;
    if (next === 'preview') {
      // El iframe se crea en este ciclo de render; hay que esperarlo para escribirle.
      setTimeout(() => this.renderPreview());
    }
  }

  /** El textarea de HTML pasó a ser la fuente de verdad: se vuelca al editor y se emite. */
  private applyHtmlSource(immediate = true): void {
    this.editableRef.nativeElement.innerHTML = this.htmlSource ?? '';
    this.decorateImageControls();
    this.updateEmpty();
    if (immediate) {
      this.flush();
    } else {
      this.scheduleEmit();
    }
  }

  onHtmlSourceInput(): void {
    // Se emite (con el mismo rebote que la vista de diseño) mientras se escribe, para que el
    // formulario padre se entere aunque el usuario guarde sin volver a la vista de diseño.
    this.applyHtmlSource(false);
  }

  /** Cuerpo del editor sin los controles que solo existen mientras se edita. */
  private currentBodyHtml(): string {
    const clone = this.editableRef.nativeElement.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('[data-rte-editor-only]').forEach(el => el.remove());
    clone.querySelectorAll('[data-rte-image]').forEach(el => el.classList.remove('rte-image-selected'));
    return clone.innerHTML;
  }

  /**
   * Vista previa en un iframe con `sandbox` vacío: sin scripts, sin formularios y sin
   * acceso al documento anfitrión. El correo se pinta tal cual lo verá el destinatario,
   * sin heredar los estilos de la aplicación.
   */
  private renderPreview(): void {
    const frame = this.previewFrameRef?.nativeElement;
    if (!frame) return;
    const body = this.buildFullHtml();
    frame.srcdoc = body.trim()
      ? `<!DOCTYPE html><html><head><meta charset="utf-8">`
        + `<style>body{margin:0;padding:16px;font-family:Arial,Helvetica,sans-serif;`
        + `font-size:14px;color:#222;background:#fff;}img{max-width:100%;}</style>`
        + `</head><body>${body}</body></html>`
      : `<!DOCTYPE html><html><body style="margin:0;padding:16px;font-family:Arial,Helvetica,sans-serif;`
        + `font-size:13px;color:#9ca3af;background:#fff;">Sin contenido para previsualizar.</body></html>`;
  }

  /**
   * INC 82 — ¿el cuerpo guardado es HTML escapado?
   *
   * Ocurre con todo lo que se pegó en el editor antes de que existiera la vista de código:
   * el navegador insertó el marcado como texto y se guardó con las entidades escapadas, así
   * que el destinatario recibía `<table …>` a la vista. Se detecta cuando el TEXTO del
   * editor contiene etiquetas y el DOM no tiene elementos de contenido reales.
   */
  private detectEscapedHtml(): void {
    const el = this.editableRef.nativeElement;
    const text = el.innerText || '';
    const looksLikeMarkup = /<\s*\/?\s*(html|head|body|table|tr|td|th|div|p|span|a|img|br|hr|h[1-6]|ul|ol|li|strong|b|em|i|style)\b[^>]*>/i.test(text);
    const hasRealElements = el.querySelector(
      'table, img, a, h1, h2, h3, h4, h5, h6, ul, ol, blockquote, strong, em, u, span[style]'
    ) !== null;
    this.escapedHtmlDetected = looksLikeMarkup && !hasRealElements;
  }

  /**
   * Interpreta como marcado el HTML que hoy se ve como texto. `innerText` (y no
   * `textContent`) porque el contenteditable envuelve cada línea pegada en su propio
   * `div`: textContent las pegaría todas juntas.
   */
  interpretAsHtml(): void {
    if (this.disabled) return;
    const el = this.editableRef.nativeElement;
    el.innerHTML = el.innerText;
    this.decorateImageControls();
    this.updateEmpty();
    this.detectEscapedHtml();
    this.flush();
  }

  private parseIncomingHtml(html: string): { attachments: AttachmentItem[]; bodyHtml: string } {
    const container = document.createElement('div');
    container.innerHTML = html;
    const header = container.querySelector('[data-rte-attachments-header]');
    const attachments: AttachmentItem[] = [];
    if (header) {
      header.querySelectorAll('a[href]').forEach(a => {
        const url = a.getAttribute('href') || '';
        const fileName = a.getAttribute('title') || a.textContent?.trim() || 'archivo';
        if (url) attachments.push({ url, fileName });
      });
      header.remove();
    }
    return { attachments, bodyHtml: container.innerHTML };
  }

  private updateEmpty(): void {
    const el = this.editableRef.nativeElement;
    this.empty = el.innerText.trim().length === 0
      && el.querySelectorAll('img').length === 0
      && this.attachments.length === 0;
  }

  private applyDisabled(): void {
    this.editableRef.nativeElement.contentEditable = this.disabled ? 'false' : 'true';
  }

  onInput(): void {
    this.updateEmpty();
    this.scheduleEmit();
  }

  private scheduleEmit(): void {
    if (this.emitTimer) clearTimeout(this.emitTimer);
    this.emitTimer = setTimeout(() => this.emitNow(), 300);
  }

  /** Fuerza la emisión inmediata (p. ej. antes de guardar). */
  flush(): void {
    if (this.emitTimer) {
      clearTimeout(this.emitTimer);
      this.emitTimer = null;
    }
    this.emitNow();
  }

  /**
   * INC 82 — el padre llama a esto antes de guardar. Si el usuario dejó abierta la vista de
   * código, lo tecleado allí todavía no está en el editor: hay que volcarlo primero o se
   * guardaría el contenido anterior.
   */
  flushForSave(): void {
    if (this.mode === 'html') {
      this.editableRef.nativeElement.innerHTML = this.htmlSource ?? '';
      this.decorateImageControls();
      this.updateEmpty();
    }
    this.flush();
  }

  private emitNow(): void {
    const html = this.buildFullHtml();
    this.lastEmitted = html;
    this.detectEscapedHtml();
    this.valueChange.emit(html);
  }

  /**
   * Combina la franja de adjuntos (si hay) + el cuerpo del editor en un solo
   * HTML — es lo que viaja como bodyHtml real de la campaña. Los controles
   * de edición de imagen (cambiar/quitar) son solo de edición y se excluyen
   * de la copia emitida; el DOM real del editor los conserva.
   */
  private buildFullHtml(): string {
    const clone = this.editableRef.nativeElement.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('[data-rte-editor-only]').forEach(el => el.remove());
    clone.querySelectorAll('[data-rte-image]').forEach(el => el.classList.remove('rte-image-selected'));
    return this.buildAttachmentsHeaderHtml() + clone.innerHTML;
  }

  // ── Selección (para que los popovers no pierdan el caret/selección) ──

  private saveSelection(): void {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && this.editableRef.nativeElement.contains(sel.anchorNode)) {
      this.savedRange = sel.getRangeAt(0).cloneRange();
    } else {
      this.savedRange = null;
    }
  }

  private restoreSelection(): void {
    this.editableRef.nativeElement.focus();
    if (!this.savedRange) return;
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(this.savedRange);
  }

  // ── Comandos de la barra de herramientas ──────────────────────────

  exec(command: string, arg?: string): void {
    if (this.disabled) return;
    this.editableRef.nativeElement.focus();
    document.execCommand(command, false, arg);
    this.updateEmpty();
    this.scheduleEmit();
  }

  openLinkPopover(): void {
    if (this.disabled) return;
    this.variablesMenuOpen = false;
    this.saveSelection();
    this.linkUrl = 'https://';
    this.linkPopoverOpen = true;
    setTimeout(() => this.linkInputRef?.nativeElement.select());
  }

  confirmLink(): void {
    const url = this.linkUrl.trim();
    this.linkPopoverOpen = false;
    if (!url) return;
    this.restoreSelection();
    this.exec('createLink', url);
  }

  toggleVariablesMenu(): void {
    this.linkPopoverOpen = false;
    this.colorMenuOpen = false;
    if (this.variablesMenuOpen) {
      this.variablesMenuOpen = false;
      return;
    }
    // Guardar el caret ANTES de abrir: al hacer clic en el botón el editor pierde
    // el foco y la variable acababa insertándose donde el navegador quisiera.
    this.saveSelection();
    this.variableSearch = '';
    this.measureVariablesMenu();
    this.variablesMenuOpen = true;
    setTimeout(() => this.variablesSearchRef?.nativeElement.focus());
  }

  /**
   * Decide hacia dónde abre el menú y cuánto puede medir su lista.
   *
   * No basta con mirar el viewport: el editor puede vivir dentro de un panel con scroll
   * propio —el inspector del constructor de chatflows deja ~520 px útiles— y ahí un menú
   * "que cabe en pantalla" se recorta igual contra el borde del panel. Por eso se mide
   * contra el ancestro scrollable, y contra la BARRA, que es a lo que está anclado.
   */
  private measureVariablesMenu(): void {
    const btn = this.variablesBtnRef?.nativeElement;
    const toolbar = (btn?.closest('.rte-toolbar') as HTMLElement | null) ?? btn;
    if (!toolbar) return;
    const rect = toolbar.getBoundingClientRect();
    const clip = this.scrollClipRect(toolbar);
    const below = clip.bottom - rect.bottom - 16;
    const above = rect.top - clip.top - 16;

    this.variablesMenuDropUp = below < 260 && above > below;
    const space = this.variablesMenuDropUp ? above : below;
    // 96px = buscador + pie del menú. Nunca por debajo de 140px: con menos, la lista
    // deja de ser usable y es preferible que el contenedor haga scroll.
    const list = Math.max(140, Math.min(320, space - 96));
    this.variablesMenuListMaxHeight = `${Math.round(list)}px`;
  }

  /** Rectángulo que de verdad recorta al menú: el ancestro con scroll, o el viewport. */
  private scrollClipRect(el: HTMLElement): { top: number; bottom: number } {
    let node = el.parentElement;
    while (node) {
      const overflowY = getComputedStyle(node).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        const rect = node.getBoundingClientRect();
        return {
          top: Math.max(0, rect.top),
          bottom: Math.min(window.innerHeight, rect.bottom),
        };
      }
      node = node.parentElement;
    }
    return { top: 0, bottom: window.innerHeight };
  }

  /** Namespaces con sus variables filtradas por `variableSearch` (clave, etiqueta o descripción). */
  get filteredNamespaces(): AvailableVariableNamespace[] {
    const q = this.variableSearch.trim().toLowerCase();
    if (!q) return this.variableNamespaces;
    return this.variableNamespaces
      .map((ns) => ({
        ...ns,
        variables: ns.variables.filter((v) =>
          `${v.key} ${v.label} ${v.description ?? ''}`.toLowerCase().includes(q)
        ),
      }))
      .filter((ns) => ns.variables.length > 0);
  }

  get visibleVariableCount(): number {
    return this.filteredNamespaces.reduce((acc, ns) => acc + ns.variables.length, 0);
  }

  get totalVariableCount(): number {
    return this.variableNamespaces.reduce((acc, ns) => acc + ns.variables.length, 0);
  }

  // ── INC 65: formato de bloque, alineación y color ─────────────────────

  /** Aplica un estilo de bloque (párrafo, título, cita) a la línea actual. */
  applyBlockFormat(value: string): void {
    if (!value) return;
    this.exec('formatBlock', `<${value}>`);
  }

  /** El `select` de formato vuelve a "Párrafo" tras aplicar: no refleja estado. */
  onBlockFormatChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.applyBlockFormat(select.value);
    select.value = '';
  }

  toggleColorMenu(): void {
    this.linkPopoverOpen = false;
    this.variablesMenuOpen = false;
    if (!this.colorMenuOpen) this.saveSelection();
    this.colorMenuOpen = !this.colorMenuOpen;
  }

  applyTextColor(color: string): void {
    this.colorMenuOpen = false;
    this.restoreSelection();
    this.exec('foreColor', color);
  }

  /** Quita negritas/colores/tamaños del texto seleccionado. */
  clearFormat(): void {
    this.exec('removeFormat');
  }

  // ── Imágenes (inline en el cuerpo) ──────────────────────────────────

  /**
   * Abre el selector de archivos. Guarda la selección/caret antes de que el
   * diálogo nativo le quite el foco al editor, para poder restaurarla al
   * insertar la imagen.
   */
  triggerImageUpload(): void {
    if (this.disabled || !this.uploadHandler || this.imageUploading) return;
    this.replaceTargetWrapper = null;
    this.variablesMenuOpen = false;
    this.linkPopoverOpen = false;
    this.imageUploadError = '';
    this.saveSelection();
    this.imageInputRef?.nativeElement.click();
  }

  /** Abre el mismo selector de archivos pero en modo "reemplazar" sobre una imagen ya insertada. */
  private triggerImageReplace(wrapper: HTMLElement): void {
    if (this.disabled || !this.uploadHandler || this.imageUploading) return;
    this.replaceTargetWrapper = wrapper;
    this.imageUploadError = '';
    this.imageInputRef?.nativeElement.click();
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    if (this.replaceTargetWrapper) {
      const wrapper = this.replaceTargetWrapper;
      this.replaceTargetWrapper = null;
      this.uploadAndReplaceImage(file, wrapper);
    } else {
      this.uploadAndInsertImage(file);
    }
  }

  private async uploadAndInsertImage(file: File): Promise<void> {
    if (!this.uploadHandler) return;
    this.restoreSelection();
    const placeholderId = this.insertUploadPlaceholder(`Subiendo imagen "${this.truncateFileName(file.name)}"…`);
    this.imageUploading = true;
    this.imageUploadError = '';
    try {
      const url = await this.uploadHandler(file);
      this.replacePlaceholder(placeholderId, this.buildImageHtml(url));
    } catch {
      this.removePlaceholder(placeholderId);
      this.imageUploadError = 'No se pudo subir la imagen. Intenta nuevamente.';
    } finally {
      this.imageUploading = false;
    }
  }

  private async uploadAndReplaceImage(file: File, wrapper: HTMLElement): Promise<void> {
    if (!this.uploadHandler) return;
    const img = wrapper.querySelector('img');
    if (img) img.style.opacity = '0.4';
    this.imageUploading = true;
    this.imageUploadError = '';
    try {
      const url = await this.uploadHandler(file);
      if (img) img.src = url;
      this.onInput();
    } catch {
      this.imageUploadError = 'No se pudo cambiar la imagen. Intenta nuevamente.';
    } finally {
      if (img) img.style.opacity = '1';
      this.imageUploading = false;
    }
  }

  /**
   * max-height evita que una imagen muy alta/vertical domine todo el editor
   * (rompe la experiencia); margin le da aire respecto al texto alrededor.
   * Se envuelve en data-rte-image (display:block, no inline-block, para que
   * el caret se comporte igual que junto a cualquier bloque) para poder
   * seleccionarla y mostrarle los controles de cambiar/quitar.
   */
  private buildImageHtml(url: string): string {
    return `<div data-rte-image="true" contenteditable="false" style="position:relative;display:block;width:max-content;max-width:100%;margin:12px 0;">
      <img src="${url}" alt="" style="max-width:100%;max-height:420px;height:auto;display:block;border-radius:8px;" />
      ${this.buildImageControlsHtml()}</div>`;
  }

  /** Botones de "cambiar imagen" y "quitar" — visibles al pasar el mouse o al seleccionar la imagen (ver estilos del componente). */
  private buildImageControlsHtml(): string {
    return `<div data-rte-editor-only="true" style="position:absolute;top:8px;right:8px;display:flex;gap:6px;">
      <button type="button" data-rte-replace="true" title="Cambiar imagen"
        style="width:28px;height:28px;border-radius:50%;background:rgba(17,24,39,0.75);color:#ffffff;border:none;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;padding:0;">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h5M20 20v-5h-5M4.5 9A7.5 7.5 0 0119 7.5M19.5 15A7.5 7.5 0 015 16.5"/></svg>
      </button>
      <button type="button" data-rte-remove="true" title="Quitar imagen"
        style="width:28px;height:28px;border-radius:50%;background:rgba(17,24,39,0.75);color:#ffffff;border:none;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;padding:0;font-size:14px;line-height:1;">×</button>
    </div>`;
  }

  /** Inyecta los controles de imagen en contenido cargado que no los tenga (HTML guardado antes de esta función). */
  private decorateImageControls(): void {
    this.editableRef.nativeElement.querySelectorAll('[data-rte-image]').forEach(wrapper => {
      if (!wrapper.querySelector('[data-rte-editor-only]')) {
        wrapper.insertAdjacentHTML('beforeend', this.buildImageControlsHtml());
      }
    });
  }

  private selectImage(wrapper: HTMLElement): void {
    if (this.selectedImageWrapper === wrapper) return;
    this.deselectImage();
    wrapper.classList.add('rte-image-selected');
    this.selectedImageWrapper = wrapper;
  }

  private deselectImage(): void {
    this.selectedImageWrapper?.classList.remove('rte-image-selected');
    this.selectedImageWrapper = null;
  }

  /**
   * Click delegado sobre el editable (solo concierne a imágenes — los
   * adjuntos viven en la franja de arriba, fuera del contenteditable):
   *  - clic en "×" → quita la imagen.
   *  - clic en "cambiar imagen" → abre el selector en modo reemplazo.
   *  - clic sobre una imagen → la selecciona (outline + controles visibles).
   *  - clic en cualquier otro lugar del editor → deselecciona.
   */
  onEditableClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    const removeBtn = target.closest('[data-rte-remove]');
    if (removeBtn) {
      event.preventDefault();
      removeBtn.closest('[data-rte-image]')?.remove();
      this.deselectImage();
      this.onInput();
      return;
    }

    const replaceBtn = target.closest('[data-rte-replace]');
    if (replaceBtn) {
      event.preventDefault();
      const wrapper = replaceBtn.closest<HTMLElement>('[data-rte-image]');
      if (wrapper) this.triggerImageReplace(wrapper);
      return;
    }

    const imageWrapper = target.closest<HTMLElement>('[data-rte-image]');
    if (imageWrapper) {
      this.selectImage(imageWrapper);
      return;
    }

    this.deselectImage();
  }

  // ── Archivos adjuntos (PDF, Word, Excel, PPT, TXT, ZIP) ─────────────
  // Viven en `attachments`, renderizados como franja arriba del cuerpo
  // (estilo Hotmail) — nunca tocan el contenteditable, así que no pueden
  // corromper listas/formato del cuerpo.

  triggerAttachmentUpload(): void {
    if (this.disabled || !this.attachmentUploadHandler) return;
    this.attachmentUploadError = '';
    this.attachmentInputRef?.nativeElement.click();
  }

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    files.forEach(file => this.uploadAttachment(file));
  }

  private async uploadAttachment(file: File): Promise<void> {
    if (!this.attachmentUploadHandler) return;
    const placeholder: AttachmentItem = { url: '', fileName: file.name, uploading: true };
    this.attachments = [...this.attachments, placeholder];
    this.attachmentUploadError = '';
    this.updateEmpty();
    try {
      const { url, fileName } = await this.attachmentUploadHandler(file);
      placeholder.url = url;
      placeholder.fileName = fileName;
      placeholder.uploading = false;
    } catch {
      this.attachments = this.attachments.filter(a => a !== placeholder);
      this.attachmentUploadError = 'No se pudo subir el archivo. Intenta nuevamente.';
    } finally {
      this.attachments = [...this.attachments];
      this.updateEmpty();
      this.scheduleEmit();
    }
  }

  removeAttachment(index: number): void {
    this.attachments = this.attachments.filter((_, i) => i !== index);
    this.updateEmpty();
    this.scheduleEmit();
  }

  badgeFor(fileName: string): { label: string; bg: string; color: string } {
    return this.fileTypeBadge(fileName);
  }

  displayName(fileName: string): string {
    return this.truncateFileName(fileName);
  }

  /** Tarjeta de adjunto a modo de link descargable, para el HTML final (estilos inline — sin Tailwind, llega tal cual a la bandeja del destinatario). */
  private buildAttachmentChipHtml(att: AttachmentItem): string {
    const badge = this.fileTypeBadge(att.fileName);
    const safeDisplayName = this.escapeHtml(this.truncateFileName(att.fileName));
    const safeFullName = this.escapeHtml(att.fileName);
    return `<a href="${att.url}" target="_blank" rel="noopener" title="${safeFullName}"
      style="display:inline-flex;align-items:center;gap:10px;padding:8px 12px;margin:0 8px 8px 0;border:1px solid #e5e7eb;border-radius:8px;background:#f9fafb;color:#374151;text-decoration:none;font-size:13px;max-width:280px;">
      <span style="flex-shrink:0;width:32px;height:32px;border-radius:6px;background:${badge.bg};color:${badge.color};display:inline-flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;font-family:Arial,sans-serif;line-height:1;">${badge.label}</span>
      <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${safeDisplayName}</span></a>`;
  }

  private buildAttachmentsHeaderHtml(): string {
    const ready = this.attachments.filter(a => a.url && !a.uploading);
    if (ready.length === 0) return '';
    const items = ready.map(a => this.buildAttachmentChipHtml(a)).join('');
    return `<div data-rte-attachments-header="true" style="margin-bottom:12px;">${items}</div>`;
  }

  /**
   * Insignia de color por extensión — mismo código de color que usa el
   * panel de documentos del editor de plantillas (getDocColorClass), pero
   * con estilos inline porque esta tarjeta termina dentro del HTML real del
   * correo (el CSS de Tailwind de la app no llega a la bandeja del destinatario).
   */
  private fileTypeBadge(fileName: string): { label: string; bg: string; color: string } {
    const ext = (fileName.split('.').pop() || '').toLowerCase();
    switch (ext) {
      case 'pdf':           return { label: 'PDF',  bg: '#fee2e2', color: '#dc2626' };
      case 'doc': case 'docx': return { label: 'DOC',  bg: '#dbeafe', color: '#2563eb' };
      case 'xls': case 'xlsx': return { label: 'XLS',  bg: '#dcfce7', color: '#16a34a' };
      case 'ppt': case 'pptx': return { label: 'PPT',  bg: '#ffedd5', color: '#ea580c' };
      case 'zip':            return { label: 'ZIP',  bg: '#ede9fe', color: '#7c3aed' };
      case 'txt':            return { label: 'TXT',  bg: '#f3f4f6', color: '#4b5563' };
      default:               return { label: 'FILE', bg: '#f3f4f6', color: '#4b5563' };
    }
  }

  /** Acorta el nombre conservando la extensión, ej: "informe-final-de-tesis.pdf" → "informe-final-de-te….pdf". */
  private truncateFileName(name: string, maxBaseLength = 22): string {
    const dotIndex = name.lastIndexOf('.');
    const hasExt = dotIndex > 0 && dotIndex < name.length - 1;
    const base = hasExt ? name.slice(0, dotIndex) : name;
    const ext = hasExt ? name.slice(dotIndex) : '';
    if (base.length <= maxBaseLength) return name;
    return `${base.slice(0, maxBaseLength)}…${ext}`;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // ── Placeholder de carga de imagen (estilo Gmail: se reemplaza al terminar) ───

  private placeholderSeq = 0;

  /** Inserta una tarjeta con spinner en el punto del caret y devuelve su id para reemplazarla luego. */
  private insertUploadPlaceholder(label: string): string {
    const id = `rte-upload-${Date.now()}-${this.placeholderSeq++}`;
    const safeLabel = this.escapeHtml(label);
    this.exec('insertHTML', `<div id="${id}" contenteditable="false"
      style="margin:8px 0;padding:8px 12px;border:1px dashed #d1d5db;border-radius:8px;background:#f9fafb;display:inline-flex;align-items:center;gap:8px;font-size:13px;color:#6b7280;">
      <svg class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
      </svg>
      <span>${safeLabel}</span></div>`);
    return id;
  }

  private replacePlaceholder(id: string, html: string): void {
    const el = this.editableRef.nativeElement.querySelector<HTMLElement>(`#${id}`);
    if (!el) return;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    el.replaceWith(...Array.from(wrapper.childNodes));
    this.onInput();
  }

  private removePlaceholder(id: string): void {
    this.editableRef.nativeElement.querySelector<HTMLElement>(`#${id}`)?.remove();
    this.onInput();
  }

  // ── Drag & drop (estilo Gmail) ───────────────────────────────────────

  onDragOver(event: DragEvent): void {
    if (this.disabled || (!this.uploadHandler && !this.attachmentUploadHandler)) return;
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(): void {
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    if (this.disabled) return;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    this.setCaretFromPoint(event.clientX, event.clientY);
    Array.from(files).forEach(file => this.handleDroppedFile(file));
  }

  private handleDroppedFile(file: File): void {
    if (file.type.startsWith('image/') && this.uploadHandler) {
      this.uploadAndInsertImage(file);
    } else if (this.attachmentUploadHandler) {
      this.uploadAttachment(file);
    }
  }

  /** Ubica el caret donde se soltó el archivo (solo relevante para imágenes, que se insertan inline). */
  private setCaretFromPoint(x: number, y: number): void {
    const doc = document as unknown as {
      caretRangeFromPoint?(x: number, y: number): Range | null;
      caretPositionFromPoint?(x: number, y: number): { offsetNode: Node; offset: number } | null;
    };

    let range: Range | null = null;
    if (doc.caretRangeFromPoint) {
      range = doc.caretRangeFromPoint(x, y);
    } else if (doc.caretPositionFromPoint) {
      const pos = doc.caretPositionFromPoint(x, y);
      if (pos) {
        range = document.createRange();
        range.setStart(pos.offsetNode, pos.offset);
        range.collapse(true);
      }
    }

    if (range && this.editableRef.nativeElement.contains(range.startContainer)) {
      this.savedRange = range;
    } else {
      this.savedRange = null;
    }
  }

  /**
   * Inserta la variable como chip atómico (no editable carácter por
   * carácter) para que no se confunda con texto plano tipo "código" — mismo
   * tratamiento visual que el editor avanzado (GrapesJS) usa para merge tags.
   */
  insertVariable(key: string): void {
    this.variablesMenuOpen = false;
    // Vuelve al caret que había antes de abrir el menú (el foco pasó al buscador).
    this.restoreSelection();
    this.exec('insertHTML', `<span class="merge-tag-chip" contenteditable="false">{{${key}}}</span>&nbsp;`);
  }
}
